// project imports
import caregiver from './caregiver';
import therapist from './therapist';
import supervisor from './supervisor';
import inclusionhead from './inclusionhead';
import admin from './admin';
import clinicalhead from './clinicalhead';
import { getAdminMenuForLevel } from './adminLevels';
import { filterMenuByVisibility, filterMenuByGrants } from './catalogueVisibility';
import { buildMenuFromGrants } from './dynamicMenu';
import { injectShareableClinical } from './clinicalShareable';

// ==============================|| MENU ITEMS ||============================== //

// Persona → static menu tree (source of correct per-role URLs). A role uses the tree of
// its `menu_template` persona, narrowed by its own grants. bcba (BCBA Head) and
// center_admin are clinical leads that reach the supervisor's screens, so they map to the
// supervisor tree rather than falling through to the therapist default (their previous,
// incorrect behaviour). super_admin/admin use the admin tree. Anything unknown falls back
// to therapist in getMenuItemsByRole.
// Personas that MUST render from their own tree even though they do not carry
// is_super_admin. The rule is not about privilege, it is about URLs: a persona with its
// own route prefix cannot be served by the grant-built menu, which takes destinations
// from the catalogue and the catalogue stores ONE route per page (the supervisor-centric
// one). That is exactly how a caregiver came to be browsing /supervisor/child — right
// capability, wrong persona's URL, and an organization-scoped screen behind it.
//
// Clinical Head and Inclusion Head are deliberately NOT here: they reach the catalogue's
// own routes today, and moving them onto their trees is a change to make on its own
// evidence rather than as a side effect of this one.
const TREE_DRIVEN_PERSONAS = new Set(['parent']);

const TREE_FOR_PERSONA = {
  supervisor,
  therapist,
  // The Caregiver persona's role code is `parent`.
  parent: caregiver,
  inclusionhead,
  clinicalhead,
  admin,
  super_admin: admin,
  bcba: supervisor,
  center_admin: supervisor
};

/**
 * Menu items for a role.
 *
 * @param {string} userRole
 * @param {string[]|null} entitledFeatures Product-tier entitlement for the
 *   signed-in organization (GET /organizations/my-features). Null means not yet
 *   known, and nothing is gated — see getAdminMenuForLevel.
 * @param {object|null} visibility Which catalogue features/pages are switched off
 *   (GET /catalogue/visibility). A SEPARATE gate from entitlement: tier says what
 *   the organization bought, this says whether the capability is on at all. Null
 *   means not yet known and hides nothing.
 */
/**
 * Drop tree items the organization's PRODUCT TIER does not cover.
 *
 * A third, independent gate, and the one the persona trees never had: Features and Pages
 * says whether a capability is switched on, Role Definitions says whether this role was
 * ticked for it, and this says whether the organization bought it. All three must pass.
 *
 * Only items carrying a `feature` (the entitlement code from
 * GET /organizations/my-features, the same code space adminLevels.js uses) are judged, so
 * a tree that declares none is returned untouched — this cannot quietly start hiding
 * screens from the personas that predate it.
 *
 * NULL entitlement means NOT YET KNOWN (request in flight, or it failed) and hides
 * nothing: blanking navigation on a network blip is far worse than briefly showing an
 * item the tier does not cover, and the API refuses it regardless.
 */
const filterMenuByTier = (items, entitledFeatures) => {
  if (!Array.isArray(entitledFeatures)) return items;
  const entitled = new Set(entitledFeatures);
  const walk = (nodes) =>
    (nodes || [])
      .filter((node) => !node?.feature || entitled.has(node.feature))
      .map((node) => (node.children ? { ...node, children: walk(node.children) } : node))
      .filter((node) => !node.children || node.children.length > 0);
  return walk(items);
};

export const getMenuItemsByRole = (userRole, entitledFeatures = null, visibility = null, myMenu = null) => {
  // 1. The three Admin Persona levels (ayro_it_admin / ayro_admin / org_admin) get a menu
  //    built from one curated definition (under-development items, platform-only gating)
  //    — see menu-items/adminLevels.js. No catalogue equivalent, so kept separate.
  //
  //    `denied_pages` is passed so this menu answers to Role Definitions the same way
  //    CatalogueGuard does. Without it the admin sidebar was the ONE menu the guard
  //    could disagree with — an item shown for a route the guard then bounced, which is
  //    how /admin/access-matrix came to redirect silently to the dashboard. Note it is
  //    the DENIALS, not granted_codes: under this model a page with no permission row is
  //    not denied, so subtracting by grants would hide items nobody un-ticked.
  //
  //    `denied_features` is passed alongside `denied_pages` because this menu keys on
  //    FEATURE codes and carries routeless "pending" items — neither of which the
  //    page-code / URL-based `denied_pages` can hide. A feature un-ticked as a whole
  //    (how the platform roles restrict) only reaches this menu through the feature list.
  const adminLevelMenu = getAdminMenuForLevel(
    userRole,
    entitledFeatures,
    visibility,
    myMenu?.denied_pages,
    myMenu?.granted_codes,
    myMenu?.denied_features
  );
  if (adminLevelMenu) {
    return { items: adminLevelMenu };
  }

  // `persona` is the template a role was created from (menu_template), falling back to
  // the role's own code. It decides which static tree (and route-guard identity) a role
  // uses. Existing persona roles have menu_template = their code.
  const persona = myMenu?.menu_template || userRole;
  const tree = TREE_FOR_PERSONA[persona];
  // A role created FROM a template — its persona differs from its own code.
  const clonedFromPersona = Boolean(myMenu?.menu_template) && myMenu.menu_template !== userRole;

  // 2. Render the PERSONA TREE (the only source of correct per-role URLs — the catalogue
  //    stores one supervisor/admin-centric route per page, so a menu built purely from
  //    grants would send a therapist to /supervisor/...), narrowed STRICTLY by grants: a
  //    coded item shows IFF its code is granted, so un-ticking a default hides it and
  //    ticking a grantable extra (added to the tree) reveals it — additive AND subtractive.
  //
  //    Used for (a) the persona roles that carry is_super_admin (Supervisor, Therapist, and
  //    mapped BCBA Head / Center Admin) — they bypass PermissionChecker, so grants are the
  //    authoritative answer for their menu — and (b) any role CREATED FROM one of those
  //    templates, which must render its template's tree at the right URLs. Roles that are
  //    their own persona and are NOT super (Clinical Head, Inclusion Head) fall through to
  //    the grant-driven path below, which is how they already worked.
  if (tree && (myMenu?.is_super_admin || clonedFromPersona || TREE_DRIVEN_PERSONAS.has(persona))) {
    // The persona tree, narrowed to what this role is granted. injectShareableClinical adds
    // the shareable clinical pages (Materials, Skills, Therapists) at this role's OWN URL,
    // where a dedicated per-persona route exists — so a role can be granted them and they
    // appear at the correct URL. filterMenuByGrants then shows each coded item iff its code
    // is granted, so un-ticking a default hides it (additive within the tree + subtractive).
    //
    // The tree is the ONLY source of sidebar items: every screen a persona can navigate to
    // has a tree entry at the right URL. Pages granted beyond it are either in-context (tabs,
    // gated where they render) or reached via the shareables above — building extra sidebar
    // items straight from the catalogue re-introduced each capability under its feature code
    // (calendar vs my_calendar), duplicating tree items, so it is deliberately not done here.
    const merged = injectShareableClinical(tree, persona);
    const personaItems = filterMenuByTier(
      filterMenuByGrants(filterMenuByVisibility([merged], visibility), myMenu?.granted_codes),
      entitledFeatures
    );

    // Administration screens (User Management, Role Definitions, …) live at ONE canonical
    // /admin URL shared by every role — unlike the role-prefixed clinical screens — so a
    // persona role an admin has granted an Administration feature can show it straight from
    // its REAL grants. Only the Administration section is appended (Clinical is already the
    // persona tree above), and it is built from granted_menu (the role's real ticks, not the
    // super-admin catch-all), so nothing shows until it is enabled in Role Definitions. The
    // matching /admin route is opened for these roles in RoleGuard by the same grant check.
    const adminGroups = filterMenuByVisibility(
      (buildMenuFromGrants({ sections: myMenu?.granted_menu }) || []).filter((g) => g.title === 'Administration'),
      visibility
    );
    return { items: [...personaItems, ...adminGroups] };
  }

  // 3. Non-super roles (Clinical Head, Inclusion Head, Caregiver, custom roles, Ayro
  //    Support) are already grant-driven: the server returns exactly what they were
  //    granted, so the sidebar is built from that directly.
  const granted = myMenu?.is_super_admin ? null : buildMenuFromGrants(myMenu);
  if (granted) {
    return { items: filterMenuByVisibility(granted, visibility) };
  }

  // 4. Fallback (lookup not landed, or a persona with no tree and no usable grants):
  //    the persona tree if known, else therapist, filtered by grants.
  const gate = (items) => filterMenuByGrants(filterMenuByVisibility(items, visibility), myMenu?.granted_codes);
  return { items: gate([tree || therapist]) };
};

// Default export for backward compatibility
const menuItems = {
  items: [therapist]
};

export default menuItems;
